import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

// Swipe configuration
const SWIPE_CONFIG = {
  threshold: 50, // Minimum distance for swipe
  velocity: 0.3, // Minimum velocity
  restraint: 100, // Maximum perpendicular distance
  allowedTime: 500, // Maximum time for swipe
  rightToLeftOnly: true // Only allow right to left swipes
};

interface CurrentPost {
  id: number;
  title: string;
  tags: number[];
  slug: string;
}

interface SwipeContextType {
  isRelatedPageVisible: boolean;
  showRelatedPage: () => void;
  hideRelatedPage: () => void;
  enableSwipe: boolean;
  setEnableSwipe: (enable: boolean) => void;
  currentPost: CurrentPost | null;
  setCurrentPost: (post: CurrentPost | null) => void;
}

const SwipeContext = createContext<SwipeContextType | undefined>(undefined);

export const useSwipe = () => {
  const context = useContext(SwipeContext);
  if (!context) {
    throw new Error('useSwipe must be used within a SwipeProvider');
  }
  return context;
};

interface SwipeProviderProps {
  children: React.ReactNode;
}

const SwipeProvider: React.FC<SwipeProviderProps> = ({ children }) => {
  const [isRelatedPageVisible, setIsRelatedPageVisible] = useState(false);
  const [enableSwipe, setEnableSwipe] = useState(true);
  const [currentPost, setCurrentPost] = useState<CurrentPost | null>(null);
  
  // Touch tracking
  const touchDataRef = useRef<{
    startX: number;
    startY: number;
    startTime: number;
    isTracking: boolean;
  }>({
    startX: 0,
    startY: 0,
    startTime: 0,
    isTracking: false
  });

  const showRelatedPage = () => {
    console.log('[SwipeProvider] 🔄 Showing related page');
    setIsRelatedPageVisible(true);
  };

  const hideRelatedPage = () => {
    console.log('[SwipeProvider] 🔄 Hiding related page');
    setIsRelatedPageVisible(false);
  };

  const handleTouchStart = (e: TouchEvent) => {
    if (!enableSwipe || isRelatedPageVisible) return;
    
    const touch = e.touches[0];
    touchDataRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      isTracking: true
    };
    
    // Touch started - removed indicator logic
    
    console.log('[SwipeProvider] 📱 Touch start detected:', {
      x: touch.clientX,
      y: touch.clientY,
      time: touchDataRef.current.startTime,
      enableSwipe,
      relatedPageVisible: isRelatedPageVisible,
      nearRightEdge: touch.clientX > window.innerWidth * 0.8
    });
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!touchDataRef.current.isTracking || !enableSwipe || isRelatedPageVisible) return;
    
    const touch = e.touches[0];
    const deltaX = touchDataRef.current.startX - touch.clientX; // Right to left = positive
    const deltaY = Math.abs(touchDataRef.current.startY - touch.clientY);
    
    // Swipe progress tracking - removed indicator logic
    if (deltaX > 0 && deltaY < SWIPE_CONFIG.restraint) {
      console.log('[SwipeProvider] 📱 Swipe in progress:', {
        deltaX,
        deltaY,
        threshold: SWIPE_CONFIG.threshold
      });
    }
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (!touchDataRef.current.isTracking || !enableSwipe || isRelatedPageVisible) {
      touchDataRef.current.isTracking = false;
      return;
    }
    
    const touch = e.changedTouches[0];
    const endTime = Date.now();
    const timeDiff = endTime - touchDataRef.current.startTime;
    const deltaX = touchDataRef.current.startX - touch.clientX; // Right to left = positive
    const deltaY = Math.abs(touchDataRef.current.startY - touch.clientY);
    
    // Calculate velocity (pixels per ms)
    const velocity = deltaX / timeDiff;
    
    console.log('[SwipeProvider] 📱 Touch end analysis:', {
      deltaX,
      deltaY,
      timeDiff,
      velocity,
      thresholds: {
        distance: SWIPE_CONFIG.threshold,
        time: SWIPE_CONFIG.allowedTime,
        velocity: SWIPE_CONFIG.velocity,
        restraint: SWIPE_CONFIG.restraint
      }
    });
    
    // Check if this qualifies as a valid right-to-left swipe
    const isValidSwipe = 
      deltaX > SWIPE_CONFIG.threshold && // Moved right to left enough
      deltaY < SWIPE_CONFIG.restraint && // Didn't move too much vertically
      timeDiff < SWIPE_CONFIG.allowedTime && // Fast enough
      velocity > SWIPE_CONFIG.velocity; // Fast enough velocity
    
    if (isValidSwipe) {
      console.log('[SwipeProvider] ✅ Valid right-to-left swipe detected! Opening related page');
      showRelatedPage();
    } else {
      console.log('[SwipeProvider] ❌ Invalid swipe:', {
        reason: deltaX <= SWIPE_CONFIG.threshold ? 'insufficient_distance' :
                deltaY >= SWIPE_CONFIG.restraint ? 'too_much_vertical' :
                timeDiff >= SWIPE_CONFIG.allowedTime ? 'too_slow' :
                velocity <= SWIPE_CONFIG.velocity ? 'insufficient_velocity' : 'unknown'
      });
    }
    
    // Reset swipe state
    touchDataRef.current.isTracking = false;
  };

  // Device detection
  const isMobile = () => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  useEffect(() => {
    // Only add swipe listeners on mobile devices
    if (!isMobile()) {
      console.log('[SwipeProvider] 💻 Desktop detected, swipe disabled');
      return;
    }

    console.log('[SwipeProvider] 📱 Mobile detected, adding swipe listeners');
    
    const options = { passive: true };
    
    document.addEventListener('touchstart', handleTouchStart, options);
    document.addEventListener('touchmove', handleTouchMove, options);
    document.addEventListener('touchend', handleTouchEnd, options);
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enableSwipe, isRelatedPageVisible]);

  // Keyboard shortcut for testing on desktop
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // R key to toggle related page (for testing)
      if (e.key === 'r' || e.key === 'R') {
        console.log('[SwipeProvider] ⌨️ R key pressed, toggling related page');
        if (isRelatedPageVisible) {
          hideRelatedPage();
        } else {
          showRelatedPage();
        }
      }
      
      // Escape key to close related page
      if (e.key === 'Escape' && isRelatedPageVisible) {
        console.log('[SwipeProvider] ⌨️ Escape key pressed, closing related page');
        hideRelatedPage();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isRelatedPageVisible]);

  const contextValue: SwipeContextType = {
    isRelatedPageVisible,
    showRelatedPage,
    hideRelatedPage,
    enableSwipe,
    setEnableSwipe,
    currentPost,
    setCurrentPost
  };

  return (
    <SwipeContext.Provider value={contextValue}>
      {children}
      {/* SwipeIndicator removed - not needed for close gestures */}
    </SwipeContext.Provider>
  );
};

export default SwipeProvider; 